import React, { useMemo, useState } from 'react';
import {
  Modal, Steps, Segmented, Select, DatePicker, Input, Button, Space, Typography,
  Alert, Checkbox, Radio, Empty, Spin, message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import i18n, { datePickerFormat, formatDate } from '../../i18n';
import {
  useCreateFreeSaveMutation,
  useListWeatherImportLocationsQuery,
  useListUserClimateMapsQuery,
} from '../../api/savesV2Api';
import { useListGardenPlansQuery } from '../../api/gardenPlansApi';
import type { WeatherImportLocation } from '../../types/simulation';
import UsLocationMap from './UsLocationMap';

const { Text, Paragraph } = Typography;

type ClimateKind = 'year' | 'random' | 'userCustom';

const measurementOptions = () => [
  { label: i18n.t('saves:shared.measurement.metric'), value: 'metric' },
  { label: i18n.t('saves:shared.measurement.imperial'), value: 'imperial' },
];

const defaultSaveName = () => i18n.t('saves:shared.defaultSaveName');

export interface GuidedSaveCreationWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: (saveId: string) => void;
}

const GuidedSaveCreationWizard: React.FC<GuidedSaveCreationWizardProps> = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation('saves');
  const [step, setStep] = useState(0);
  const [locationKey, setLocationKey] = useState<string | null>(null);
  const [climateKind, setClimateKind] = useState<ClimateKind>('year');
  const [year, setYear] = useState<number | null>(null);
  const [seed, setSeed] = useState('');
  const [customMapId, setCustomMapId] = useState<string | null>(null);
  const [fromPlan, setFromPlan] = useState(false);
  const [planVersionId, setPlanVersionId] = useState<string | null>(null);
  const [planTargetYear, setPlanTargetYear] = useState(dayjs().year());
  const [applyPlantings, setApplyPlantings] = useState(true);
  const [name, setName] = useState(defaultSaveName);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs());
  const [measurementSystem, setMeasurementSystem] = useState('metric');
  const [description, setDescription] = useState('');

  const { data: catalog, isFetching: catalogFetching } = useListWeatherImportLocationsQuery(undefined, { skip: !open });
  const locations = useMemo(() => catalog?.locations ?? [], [catalog]);
  const recentYears = useMemo(() => catalog?.recentCompleteYears ?? [], [catalog]);
  const selectedLocation = useMemo<WeatherImportLocation | null>(
    () => locations.find((l) => l.key === locationKey) ?? null,
    [locations, locationKey],
  );

  const { data: userMaps = [], isFetching: userMapsFetching } = useListUserClimateMapsQuery(
    selectedLocation ? { lat: selectedLocation.latitude, lon: selectedLocation.longitude } : undefined,
    { skip: !open || climateKind !== 'userCustom' },
  );

  const { data: gardenPlans = [], isFetching: plansFetching } = useListGardenPlansQuery(
    { status: 'active' },
    { skip: !open || !fromPlan },
  );
  const selectedPlan = useMemo(
    () => gardenPlans.find((plan) => plan.latestVersionId === planVersionId) || null,
    [gardenPlans, planVersionId],
  );

  const [createFreeSave, { isLoading: creating }] = useCreateFreeSaveMutation();

  const yearOptions = useMemo(() => {
    const current = dayjs().year();
    const years = Array.from(new Set([current, ...recentYears])).sort((a, b) => b - a);
    return years.map((y) => ({ value: y, label: y === current ? t('wizard.currentYear', { year: y }) : String(y) }));
  }, [recentYears, t]);

  const reset = () => {
    setStep(0);
    setLocationKey(null);
    setClimateKind('year');
    setYear(null);
    setSeed('');
    setCustomMapId(null);
    setFromPlan(false);
    setPlanVersionId(null);
    setPlanTargetYear(dayjs().year());
    setApplyPlantings(true);
    setName(defaultSaveName());
    setStartDate(dayjs());
    setMeasurementSystem('metric');
    setDescription('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSelectLocation = (loc: WeatherImportLocation) => {
    setLocationKey(loc.key);
  };

  const handleYearChange = (value: number) => {
    setYear(value);
    // Align the save's start year with the chosen climate year (keep month/day).
    setStartDate((prev) => (prev ? prev.year(value) : dayjs(`${value}-04-15`)));
  };

  const climateValid =
    climateKind === 'year'
      ? true
      : climateKind === 'random'
        ? true
        : Boolean(customMapId);

  const canNext =
    step === 0 ? Boolean(locationKey)
      : step === 1 ? climateValid
        : step === 2 ? (!fromPlan || Boolean(planVersionId))
          : true;

  const buildClimateSource = (): Record<string, unknown> => {
    if (climateKind === 'random') return seed ? { kind: 'random', seed } : { kind: 'random' };
    if (climateKind === 'userCustom') return { kind: 'userCustom', customMapId };
    return year ? { kind: 'year', year } : { kind: 'year' };
  };

  const handleCreate = async () => {
    if (!locationKey) return;
    const payload: Record<string, unknown> = {
      name: name.trim() || defaultSaveName(),
      description: description || '',
      locationKey,
      climateSource: buildClimateSource(),
    };
    if (fromPlan && planVersionId) {
      payload.plan = {
        versionId: planVersionId,
        targetYear: planTargetYear,
        layers: { topology: true, plantings: applyPlantings && Boolean(selectedPlan?.latestVersionSummary?.layers.plantings) },
      };
    } else {
      payload.startDate = startDate.format('YYYY-MM-DD');
      payload.measurementSystem = measurementSystem;
    }

    try {
      const result = await createFreeSave(payload).unwrap();
      const saveId = result?.save?.id;
      message.success(t('shared.created'));
      reset();
      onClose();
      if (saveId) onCreated(saveId);
    } catch (err) {
      const e = err as { data?: { message?: string } };
      message.error(e?.data?.message || t('shared.createFailed'));
    }
  };

  const steps = [
    {
      title: t('wizard.steps.location'),
      content: (
        <div>
          <Paragraph type="secondary">
            {t('wizard.locationHint')}
          </Paragraph>
          {catalogFetching ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
          ) : (
            <>
              <UsLocationMap locations={locations} selectedKey={locationKey} onSelect={handleSelectLocation} />
              <Space style={{ marginTop: 12 }} direction="vertical" size={4} align="start">
                <Select
                  style={{ width: 320 }}
                  placeholder={t('wizard.pickFromList')}
                  value={locationKey ?? undefined}
                  onChange={(v) => setLocationKey(v)}
                  options={locations.map((l) => ({ value: l.key, label: l.name }))}
                  showSearch
                  optionFilterProp="label"
                />
                {selectedLocation && (
                  <Text type="secondary">{selectedLocation.region || selectedLocation.name}</Text>
                )}
              </Space>
            </>
          )}
        </div>
      ),
    },
    {
      title: t('wizard.steps.climate'),
      content: (
        <div>
          <Segmented
            block
            value={climateKind}
            onChange={(v) => setClimateKind(v as ClimateKind)}
            options={[
              { label: t('wizard.climate.year'), value: 'year' },
              { label: t('wizard.climate.random'), value: 'random' },
              { label: t('wizard.climate.userCustom'), value: 'userCustom' },
            ]}
          />
          <div style={{ marginTop: 16 }}>
            {climateKind === 'year' && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>{t('wizard.climate.yearLabel')}</Text>
                <Select
                  style={{ width: 220 }}
                  placeholder={t('wizard.climate.selectYear')}
                  value={year ?? undefined}
                  onChange={handleYearChange}
                  options={yearOptions}
                />
                <Text type="secondary">
                  {t('wizard.climate.futureDaysHint')}
                </Text>
              </Space>
            )}
            {climateKind === 'random' && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message={t('wizard.climate.randomTitle')}
                  description={t('wizard.climate.randomDescription')}
                />
                <Input
                  style={{ width: 260 }}
                  placeholder={t('wizard.climate.seedPlaceholder')}
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                />
              </Space>
            )}
            {climateKind === 'userCustom' && (
              userMapsFetching ? (
                <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
              ) : userMaps.length === 0 ? (
                <Empty description={t('wizard.climate.noUserMaps')} />
              ) : (
                <Select
                  style={{ width: 360 }}
                  placeholder={t('wizard.climate.selectUserMap')}
                  value={customMapId ?? undefined}
                  onChange={(v) => setCustomMapId(v)}
                  options={userMaps.map((m) => ({ value: m.id, label: m.name }))}
                />
              )
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('wizard.steps.plan'),
      content: (
        <div>
          <Paragraph type="secondary">
            {t('wizard.planHint')}
          </Paragraph>
          <Radio.Group
            value={fromPlan ? 'plan' : 'blank'}
            onChange={(e) => setFromPlan(e.target.value === 'plan')}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: t('wizard.blankPlot'), value: 'blank' },
              { label: t('wizard.fromPlan'), value: 'plan' },
            ]}
          />
          {fromPlan && (
            <div style={{ marginTop: 16 }}>
              {plansFetching ? (
                <Spin />
              ) : gardenPlans.length === 0 ? (
                <Empty description={t('wizard.noPlans')} />
              ) : (
                <Select
                  style={{ width: 360 }}
                  placeholder={t('wizard.selectPlan')}
                  value={planVersionId ?? undefined}
                  onChange={(v) => setPlanVersionId(v)}
                  options={gardenPlans.map((plan) => ({
                    value: plan.latestVersionId,
                    label: t('wizard.planOption', { name: plan.name, count: plan.latestVersionSummary?.counts.beds ?? 0 }),
                  }))}
                />
              )}
              {selectedPlan?.latestVersionSummary?.layers.plantings && (
                <div style={{ marginTop: 12 }}>
                  <Checkbox checked={applyPlantings} onChange={(e) => setApplyPlantings(e.target.checked)}>
                    {t('wizard.applyPlantings')}
                  </Checkbox>
                </div>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('wizard.steps.settings'),
      content: (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div>
            <Text>{t('shared.saveName')}</Text>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {fromPlan ? (
            <div>
              <Text>{t('wizard.planTargetYear')}</Text>
              <Input
                type="number"
                min={1900}
                max={2200}
                value={planTargetYear}
                onChange={(e) => setPlanTargetYear(Number(e.target.value))}
              />
              <Text type="secondary">{t('wizard.planStartsOn', { date: formatDate(`${String(planTargetYear).padStart(4, '0')}-01-01`, 'long') })}</Text>
            </div>
          ) : (
            <div>
              <Text>{t('shared.startDate')}</Text>
              <DatePicker
                style={{ width: '100%' }}
                value={startDate}
                format={datePickerFormat()}
                onChange={(d) => d && setStartDate(d)}
                allowClear={false}
              />
            </div>
          )}
          <div>
            <Text>{t('shared.measurementSystem')}</Text>
            <Segmented
              block
              options={measurementOptions()}
              value={fromPlan
                ? (selectedPlan?.latestVersionSummary?.planScale?.preset?.startsWith('imperial') ? 'imperial' : 'metric')
                : measurementSystem}
              disabled={fromPlan}
              onChange={(v) => setMeasurementSystem(v as string)}
            />
            <Text type="secondary">{fromPlan ? t('wizard.scaleFromPlan') : t('shared.cannotChangeLater')}</Text>
          </div>
          <div>
            <Text>{t('common:labels.description')}</Text>
            <Input.TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={t('wizard.title')}
      open={open}
      onCancel={handleClose}
      width={720}
      footer={
        <Space>
          <Button onClick={handleClose}>{t('common:actions.cancel')}</Button>
          {step > 0 && <Button onClick={() => setStep((s) => s - 1)}>{t('common:actions.back')}</Button>}
          {step < steps.length - 1 ? (
            <Button type="primary" disabled={!canNext} onClick={() => setStep((s) => s + 1)}>
              {t('common:actions.next')}
            </Button>
          ) : (
            <Button type="primary" loading={creating} disabled={!locationKey} onClick={handleCreate}>
              {t('common:actions.create')}
            </Button>
          )}
        </Space>
      }
    >
      <Steps current={step} size="small" items={steps.map((s) => ({ title: s.title }))} style={{ marginBottom: 20 }} />
      <div style={{ minHeight: 320 }}>{steps[step].content}</div>
    </Modal>
  );
};

export default GuidedSaveCreationWizard;
